import { Box, Col, Row } from '@opendoor/atoms/box';
import { Grid } from '@opendoor/atoms/grid';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

const PRESET = 'square-256';

// Frosted "Featured in" bar: a translucent neutral fill + 20px blur with a 20px
// corner radius. There's no Atoms semantic token / radius-scale step for these,
// so they're inline arbitrary values rather than a CSS module.
const GLASS = 'bg-[rgba(242,241,240,0.64)] backdrop-blur-[20px]';

const createLogoUrl = (baseUrl: string, dpr: number) => {
  const params = new URLSearchParams({
    preset: PRESET,
    service: 'cosmos',
    dpr: `${dpr}`,
  });
  return `${baseUrl}?${params.toString()}`;
};

const FORBES_LOGO_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-07-1690500903565-83391.png';

const FORTUNE_LOGO_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-07-1690500903560-61291.png';

const NEW_YORK_TIMES_LOGO_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-07-1690500903555-88900.png';

const WALL_STREET_JOURNAL_LOGO_URL =
  'https://images.opendoor.com/source/s3/imgdrop-production/2023-07-1690500903562-10942.png';

const PRESS_LOGOS = [
  NEW_YORK_TIMES_LOGO_URL,
  FORBES_LOGO_URL,
  WALL_STREET_JOURNAL_LOGO_URL,
  FORTUNE_LOGO_URL,
];

interface PressBoxProps {
  className?: string;
  /** Round the bar's corners at every breakpoint (default only rounds from SM up). */
  roundedOnMobile?: boolean;
}

export const PressBox = ({ className, roundedOnMobile }: PressBoxProps) => {
  return (
    <Col className="w-full justify-center lg:w-auto">
      <Row
        className={cn(
          'w-full flex-wrap items-center justify-center gap-x-3 p-5 md:w-auto',
          GLASS,
          roundedOnMobile ? 'rounded-[20px]' : 'md:rounded-[20px]',
          className,
        )}
      >
        <Grid className="w-full grid-cols-[repeat(3,max-content)] items-center justify-center justify-items-center gap-6">
          <Text
            as="span"
            className="text-body-md text-od-content-inactive-primary max-md:w-full max-md:text-center"
          >
            Featured in:
          </Text>
          {PRESS_LOGOS.map((photoUrl) => {
            const srcSet = [
              `${createLogoUrl(photoUrl, 1)} 1x`,
              `${createLogoUrl(photoUrl, 2)} 2x`,
              `${createLogoUrl(photoUrl, 3)} 3x`,
            ].join(', ');
            return (
              <Box key={photoUrl} className="h-5 w-auto justify-center">
                <Image
                  src={createLogoUrl(photoUrl, 1)}
                  srcSet={srcSet}
                  alt=""
                  loading="eager"
                  className="h-full w-auto object-contain"
                />
              </Box>
            );
          })}
        </Grid>
      </Row>
    </Col>
  );
};
